// PersonaSetup.jsx - Configure persona contacts for email notifications
import React, { useState } from 'react';
import { 
  Users, Mail, Phone, Building, Shield, Scale, Crown, 
  CheckCircle, AlertTriangle, Save, Send, ArrowRight, 
  User, Plus, Trash2, Edit3
} from 'lucide-react';
import { Card } from './ui/Card';
import { Button, PrimaryButton, SecondaryButton } from './ui/Button';
import { Badge } from './ui/Badge';
import apiService from '../services/api';

// Persona configuration
const PERSONA_CONFIG = {
  c_level_tech: {
    id: 'c_level_tech',
    name: 'C-Level Technology',
    description: 'CTO, CIO, Chief Technology Officer',
    icon: <Building aria-hidden="true" className="w-5 h-5" />,
    color: 'blue',
    placeholder: {
      name: 'John Smith',
      title: 'Chief Technology Officer',
      email: 'cto@company.com'
    }
  },
  c_level_hr: {
    id: 'c_level_hr',
    name: 'Human Resources',
    description: 'HR Director, People Operations',
    icon: <Users aria-hidden="true" className="w-5 h-5" />,
    color: 'green',
    placeholder: {
      name: 'Sarah Johnson',
      title: 'HR Director',
      email: 'hr@company.com'
    }
  },
  legal: {
    id: 'legal',
    name: 'Legal & Compliance',
    description: 'General Counsel, Compliance Officer',
    icon: <Scale className="w-5 h-5" />,
    color: 'purple',
    placeholder: {
      name: 'Michael Brown',
      title: 'General Counsel',
      email: 'legal@company.com'
    }
  },
  ceo_board: {
    id: 'ceo_board',
    name: 'CEO/Board Level',
    description: 'CEO, Board Members, Executive Leadership',
    icon: <Crown className="w-5 h-5" />,
    color: 'gold',
    placeholder: {
      name: 'Lisa Davis',
      title: 'Chief Executive Officer',
      email: 'ceo@company.com'
    }
  }
};

const PersonaSetup = ({ sessionId, companyName, onComplete, onCancel }) => {
  const [personas, setPersonas] = useState(() => {
    // Initialize with empty persona data
    const initialPersonas = {};
    Object.keys(PERSONA_CONFIG).forEach(personaId => {
      initialPersonas[personaId] = {
        name: '',
        email: '',
        title: '',
        phone: '',
        isPrimary: false
      };
    });
    return initialPersonas;
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('setup'); // setup, confirm, sending

  const updatePersona = (personaId, field, value) => {
    setPersonas(prev => ({
      ...prev,
      [personaId]: {
        ...prev[personaId],
        [field]: value
      }
    }));
  };

  const validateForm = () => {
    const errors = [];
    
    Object.entries(personas).forEach(([personaId, persona]) => {
      const config = PERSONA_CONFIG[personaId];
      
      if (!persona.name.trim()) {
        errors.push(`${config.name} name is required`);
      }
      
      if (!persona.email.trim()) {
        errors.push(`${config.name} email is required`);
      } else if (!/\S+@\S+\.\S+/.test(persona.email)) {
        errors.push(`${config.name} email is invalid`);
      }
    });

    // Check for duplicate emails
    const emails = Object.values(personas).map(p => p.email.toLowerCase()).filter(Boolean);
    const duplicates = emails.filter((email, index) => emails.indexOf(email) !== index);
    if (duplicates.length > 0) {
      errors.push('Duplicate email addresses are not allowed');
    }

    return errors;
  };

  const setupPersonaContacts = async () => {
    try {
      setSaving(true);
      setError(null);

      console.log('📧 Setting up persona contacts for session:', sessionId);

      // Setup persona contacts in the backend
      const response = await apiService.setupPersonaContacts(sessionId, personas);
      
      if (response.success) {
        console.log('✅ Persona contacts set up successfully');
        setStep('confirm');
      } else {
        throw new Error(response.message || 'Failed to set up persona contacts');
      }

    } catch (error) {
      console.error('❌ Error setting up persona contacts:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const sendInvitations = async () => {
    try {
      setLoading(true);
      setError(null);
      setStep('sending');

      console.log('📧 Sending assessment invitations...');

      const response = await apiService.sendAssessmentInvitations(sessionId);
      
      if (response.success) {
        console.log('✅ Invitations sent successfully:', response.data);
        
        // Show results and complete setup
        setTimeout(() => {
          onComplete({
            invitationsSent: true,
            results: response.data,
            personas: personas
          });
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to send invitations');
      }

    } catch (error) {
      console.error('❌ Error sending invitations:', error);
      setError(error.message);
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setupPersonaContacts();
  };

  if (step === 'sending') {
    return (
      <div className="min-h-screen bg-[rgba(255,255,255,0.03)] flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <Card.Body className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-lg font-semibold text-[#e6f1ff] mb-2">Sending Invitations</h2>
            <p className="text-[#a8b2d1]">
              Sending assessment invitations to all personas...
            </p>
            <div className="mt-6 space-y-2">
              {Object.entries(personas).map(([personaId, persona]) => (
                <div key={personaId} className="flex items-center justify-between text-sm">
                  <span>{PERSONA_CONFIG[personaId].name}</span>
                  <span className="text-[#8892b0]">{persona.email}</span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="min-h-screen bg-[rgba(255,255,255,0.03)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <Card.Header>
              <Card.Title icon={<Send />}>
                Ready to Send Assessment Invitations
              </Card.Title>
              <Card.Description>
                Review the persona contacts and send assessment invitations
              </Card.Description>
            </Card.Header>
            <Card.Body>
              <div className="space-y-6">
                <div className="bg-green-50 border border-[rgba(0,255,136,0.25)] rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle aria-hidden="true" className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-medium text-[#e6f1ff]">
                      Persona contacts configured successfully
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(personas).map(([personaId, persona]) => {
                    const config = PERSONA_CONFIG[personaId];
                    return (
                      <div key={personaId} className="border border-[rgba(255,255,255,0.12)] rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className={`p-2 rounded-lg bg-${config.color}-100 text-${config.color}-600`}>
                            {config.icon}
                          </div>
                          <div>
                            <h4 className="font-medium text-[#e6f1ff]">{config.name}</h4>
                            <p className="text-sm text-[#a8b2d1]">{persona.title}</p>
                          </div>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center">
                            <User aria-hidden="true" className="w-4 h-4 text-[#5a6a8a] mr-2" />
                            <span>{persona.name}</span>
                          </div>
                          <div className="flex items-center">
                            <Mail aria-hidden="true" className="w-4 h-4 text-[#5a6a8a] mr-2" />
                            <span>{persona.email}</span>
                          </div>
                          {persona.phone && (
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 text-[#5a6a8a] mr-2" />
                              <span>{persona.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-blue-50 border border-[rgba(0,217,255,0.25)] rounded-lg p-4">
                  <h4 className="font-medium text-[#e6f1ff] mb-2">What happens next?</h4>
                  <ul className="text-sm text-[#ccd6f6] space-y-1">
                    <li>• Each persona will receive an email invitation to complete their assessment</li>
                    <li>• They can access their specific questions using the link in the email</li>
                    <li>• Progress is tracked and notifications are sent as each persona completes</li>
                    <li>• Results will be available when all personas have finished</li>
                  </ul>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertTriangle aria-hidden="true" className="w-5 h-5 text-red-600 mr-2" />
                      <span className="font-medium text-[#e6f1ff]">Error</span>
                    </div>
                    <p className="text-[#ccd6f6] mt-1">{error}</p>
                  </div>
                )}
              </div>
            </Card.Body>
            <Card.Footer>
              <div className="flex justify-between">
                <SecondaryButton onClick={() => setStep('setup')}>
                  Back to Edit
                </SecondaryButton>
                <div className="space-x-3">
                  <Button variant="ghost" onClick={onCancel}>
                    Cancel
                  </Button>
                  <PrimaryButton 
                    onClick={sendInvitations} 
                    disabled={loading}
                    icon={<Send />}
                  >
                    {loading ? 'Sending...' : 'Send Invitations'}
                  </PrimaryButton>
                </div>
              </div>
            </Card.Footer>
          </Card>
        </div>
      </div>
    );
  }

  // Setup step - main form
  return (
    <div className="min-h-screen bg-[rgba(255,255,255,0.03)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <Card>
            <Card.Header>
              <Card.Title icon={<Users />}>
                Setup Persona Contacts
              </Card.Title>
              <Card.Description>
                Configure contact information for each persona who will participate in the assessment for {companyName}
              </Card.Description>
            </Card.Header>
            <Card.Body>
              <div className="space-y-8">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertTriangle aria-hidden="true" className="w-5 h-5 text-red-600 mr-2" />
                      <span className="font-medium text-[#e6f1ff]">Error</span>
                    </div>
                    <p className="text-[#ccd6f6] mt-1">{error}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {Object.entries(PERSONA_CONFIG).map(([personaId, config]) => (
                    <Card key={personaId} className="border-2 border-[rgba(255,255,255,0.12)]">
                      <Card.Header>
                        <div className="flex items-center space-x-3">
                          <div className={`p-3 rounded-lg bg-${config.color}-100 text-${config.color}-600`}>
                            {config.icon}
                          </div>
                          <div>
                            <h2 className="font-semibold text-[#e6f1ff]">{config.name}</h2>
                            <p className="text-sm text-[#a8b2d1]">{config.description}</p>
                          </div>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-[#ccd6f6] mb-1">
                              Full Name *
                            </label>
                            <input
                              type="text"
                              value={personas[personaId].name}
                              onChange={(e) => updatePersona(personaId, 'name', e.target.value)}
                              placeholder={config.placeholder.name}
                              aria-label={`${config.name} Full Name`}
                              className="w-full p-3 border border-[rgba(255,255,255,0.18)] rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)] focus:border-[#00d9ff]"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-[#ccd6f6] mb-1">
                              Email Address *
                            </label>
                            <input
                              type="email"
                              value={personas[personaId].email}
                              onChange={(e) => updatePersona(personaId, 'email', e.target.value)}
                              placeholder={config.placeholder.email}
                              aria-label={`${config.name} Email Address`}
                              className="w-full p-3 border border-[rgba(255,255,255,0.18)] rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)] focus:border-[#00d9ff]"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-[#ccd6f6] mb-1">
                              Job Title
                            </label>
                            <input
                              type="text"
                              value={personas[personaId].title}
                              onChange={(e) => updatePersona(personaId, 'title', e.target.value)}
                              placeholder={config.placeholder.title}
                              aria-label={`${config.name} Job Title`}
                              className="w-full p-3 border border-[rgba(255,255,255,0.18)] rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)] focus:border-[#00d9ff]"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-[#ccd6f6] mb-1">
                              Phone Number (Optional)
                            </label>
                            <input
                              type="tel"
                              value={personas[personaId].phone}
                              onChange={(e) => updatePersona(personaId, 'phone', e.target.value)}
                              placeholder="+1 (555) 123-4567"
                              aria-label={`${config.name} Phone Number`}
                              className="w-full p-3 border border-[rgba(255,255,255,0.18)] rounded-lg focus:ring-2 focus:ring-[rgba(0,217,255,0.5)] focus:border-[#00d9ff]"
                            />
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>

                <div className="bg-blue-50 border border-[rgba(0,217,255,0.25)] rounded-lg p-4">
                  <h4 className="font-medium text-[#e6f1ff] mb-2">📧 Email Notifications</h4>
                  <p className="text-sm text-[#ccd6f6]">
                    Each persona will receive an email invitation with a personalized link to complete their portion of the assessment. 
                    Notifications are sent automatically as each persona completes their section.
                  </p>
                </div>
              </div>
            </Card.Body>
            <Card.Footer>
              <div className="flex justify-between">
                <Button variant="ghost" onClick={onCancel}>
                  Cancel
                </Button>
                <PrimaryButton 
                  type="submit"
                  disabled={saving}
                  icon={<ArrowRight />}
                >
                  {saving ? 'Setting Up...' : 'Setup Contacts & Continue'}
                </PrimaryButton>
              </div>
            </Card.Footer>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default PersonaSetup;